import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { Box, Chip, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Button, COLORS, Icon, ICONS, Modal } from "abs-ui-components";
import "./FuelEuEmissionFactors.scss";
import { API_ROUTES } from "../../FeuApiRoutes/FeuApiRoutes";
import { emitToasterMsg } from "../../../../../services/toasterUtility";
import { callApi } from "../../../../../services/MonitoringPlanService";
import { preventScientificNotation } from "../../../../../services/utilities";
import { frameworks, gases } from "../../FeuConstants";
import { Children } from "react";

const FuelEuEmissionFactors = forwardRef(({ b31EmissionFactors, fuelOriginMsts, fuelBunkerDensities, refetchMonitoringData,vesselMstId }, ref) => {

	const RowActions = {
		Edit: 0,
		Add: 1,
	};
	const FUEL_EU_FRAMEWORK = "Fuel EU";
	const [activeAction, setactiveAction] = useState(-1);
	const [emissionFactorData, setEmissionFactorData] = useState([]);
	const [validationErrors, setValidationErrors] = useState({});
	const [fuelData, setFuelData] = useState([]);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [deletingRowIndex, setDeletingRowIndex] = useState();

	useEffect(() => {
		const transformed = transformData(b31EmissionFactors || []);
		setFuelData(transformed);
	}, [b31EmissionFactors]);

	const handleDeleteBtnClick = (rowIndex) => {
		setConfirmDelete(true);
		setDeletingRowIndex(rowIndex);
	};


	const transformData = (data = []) => {
		const getFrameworkData = (arr = [], framework) => arr.find((x) => x.legalFrameWork === framework) || {};
		const mapGases = (item, gas) => {
			const d = getFrameworkData(item[gas], FUEL_EU_FRAMEWORK);
			return {
				[FUEL_EU_FRAMEWORK]: {
					factorReference: d.factorReference || "Default",
					ttwEf: d.ttwEf ?? 0,
					slipEf: d.slipEf ?? 0,
					isTtwEditable: !!d.isTtwEditable,
					isSlipEditable: !!d.isSlipEditable,
					legalFrameWork: FUEL_EU_FRAMEWORK,
					methodologiesForEFs: d.methodologiesForEFs || "",
				},
			};
		};

		return data?.map((item) => {
			return {
				b4EmissionFactorId: item.b4EmissionFactorId ?? 0,
				fuelOrigin: item.fuelOrigin,
				fuelType: item.fuelType,
				nameOfOtherFuel: item.nameOfOtherFuel || "",
				bunkeredFuelDensity: item.bunkeredFuelDensity || [],
				fuelDescription: item.fuelDescription || "",
				co2: mapGases(item, "co2", false),
				ch4: mapGases(item, "ch4", false),
				n2o: mapGases(item, "n2o", false),
				wttEf: item.wttEf || 0,
				wttValueType: item.wttValueType || "Default",
			};
		});
	};

	const handleFuelActions = (table, row, action) => {
		if (activeAction !== -1) return;
		const isEdit = action === RowActions.Edit;

		setactiveAction(action);

		if (isEdit) {
			setValidationErrors({});
			const original = row.original;
			const emissions = gases.reduce((acc, gas) => {
				const euData = original[gas]?.[FUEL_EU_FRAMEWORK] || {};
				acc[gas] = {
					[FUEL_EU_FRAMEWORK]: {
						legalFrameWork: FUEL_EU_FRAMEWORK,
						factorReference: euData.factorReference ?? "Default",
						ttwEf: euData.ttwEf ?? "",
						slipEf: euData.slipEf ?? "",
						methodologiesForEFs: euData.methodologiesForEFs ?? "",
						isTtwEditable: euData.isTtwEditable ?? false,
						isSlipEditable: euData.isSlipEditable ?? false,
					},
				};
				return acc;
			}, {});
			setEmissionFactorData({
				...original,
				bunkeredFuelDensity: original.bunkeredFuelDensity || [],
				fuelDescription: original.fuelDescription || "",
				nameOfOtherFuel: original.nameOfOtherFuel || "",
				wttEf: original.wttEf ?? 0,
				wttValueType: original.wttValueType || "Default",
				...emissions,
			});
			setTimeout(() => table.setEditingRow(row), 0);
		} else {
			// Add new row defaults
			setValidationErrors({});
			const defaultRow = {
				fuelOrigin: "",
				fuelType: "",
				nameOfOtherFuel: "",
				bunkeredFuelDensity: [],
				fuelDescription: "",
				wttEf: 0,
				wttValueType: "Default",
				// Initialize Fuel EU emissions fields for each gas
				...gases.reduce((acc, gas) => {
					acc[gas] = {
						[FUEL_EU_FRAMEWORK]: {
							legalFrameWork: FUEL_EU_FRAMEWORK,
							ttwEf: "",
							slipEf: "",
							factorReference: "Default",
							methodologiesForEFs: "",
							isTtwEditable: false,
						},
					};
					return acc;
				}, {}),
			};
			setEmissionFactorData(defaultRow);
			table.setCreatingRow(true);
		}
	};

const resetActiveRow = (row) => {
 
    setactiveAction(-1);
    setValidationErrors({});
    setEmissionFactorData(null); 
};

	const validateRequired = (value) => (typeof value === "string" ? value?.length > 0 : typeof value === "number" ? value >= 0 : false);

	const validateFuel = (row = {}, type, rowObject) => {
		const errors = {
			fuelOrigin: !validateRequired(row.fuelOrigin) ? "Field is Required" : "",
			fuelType: !validateRequired(row.fuelType) ? "Field is Required" : "",
			nameOfOtherFuel: "",
		};
		const isOther = row.fuelOrigin?.toLowerCase().includes("biofuel") && row.fuelType?.toLowerCase().includes("other");

		if (isOther && !row.nameOfOtherFuel?.trim()) {
			errors.nameOfOtherFuel = "This field is required";
		}
		if (!row.fuelOrigin || !row.fuelType) return errors;
		const isDuplicate = (fuelData || []).some((fuel) => {
			const isSameCombination = fuel.fuelOrigin === row.fuelOrigin && fuel.FuelType === row.fuelType;
			if (type === "edit") {
				return isSameCombination && fuel.b4EmissionFactorId !== rowObject?.original?.b4EmissionFactorId;
			}
			return isSameCombination;
		});
		if (isDuplicate) {
			errors.fuelType = "Fuel Type is already in use";
		}
		return errors;
	};

	const buildFuelPayloadFromValues = (values) => {
		const { fuelType, fuelOrigin, b4EmissionFactorId, nameOfOtherFuel, bunkeredFuelDensity, wttEf, fuelDescription } = values;

		const extractGasData = (gas) => {
			// nested object (when fuel type changed)
			const nested = values[gas] && values[gas][FUEL_EU_FRAMEWORK];
			const flatPrefix = `${gas}.${FUEL_EU_FRAMEWORK}`;

			const ttwEfVal = nested?.ttwEf ?? (values[`${flatPrefix}.ttwEf`] != null && parseFloat(values[`${flatPrefix}.ttwEf`])) ?? 0;

			const slipEfVal = nested?.slipEf ?? (values[`${flatPrefix}.slipEf`] != null && parseFloat(values[`${flatPrefix}.slipEf`])) ?? 0;

			const factorReferenceVal = nested?.factorReference ?? values[`${flatPrefix}.factorReference`] ?? "Default";

			const methodologies = nested?.methodologiesForEFs ?? values[`${flatPrefix}.methodologiesForEFs`] ?? "";

			return {
				legalFrameWork: FUEL_EU_FRAMEWORK,
				ttwEf: ttwEfVal,
				slipEf: slipEfVal,
				factorReference: factorReferenceVal,
				isTtwEditable: nested?.isTtwEditable ?? true,
				isSlipEditable: nested?.isSlipEditable ?? false,
				methodologiesForEFs: methodologies,
			};
		};

		return [
			{
				b4EmissionFactorId: b4EmissionFactorId ?? 0,
				fuelOrigin,
				fuelType,
				nameOfOtherFuel: nameOfOtherFuel ?? "",
				bunkeredFuelDensity: Array.isArray(bunkeredFuelDensity)
					? bunkeredFuelDensity
					: bunkeredFuelDensity != null
						? [String(bunkeredFuelDensity)]
						: [],
				wttEf: parseFloat(wttEf ?? 0),
				fuelDescription: fuelDescription ?? "",
				co2: [extractGasData("co2")],
				ch4: [extractGasData("ch4")],
				n2o: [extractGasData("n2o")],
			},
		];
	};

	useImperativeHandle(ref, () => ({
		submitAndCheck: async () => {
			const { creatingRow, editingRow } = table.getState();

			if (!creatingRow && !editingRow) return true;

			const editedValues = creatingRow
				? creatingRow._valuesCache
				: editingRow._valuesCache;

			const mode = creatingRow ? "add" : "edit";
			const validationErrors = validateFuel(editedValues, mode, {});
			if (Object.values(validationErrors).some(Boolean)) {
				setValidationErrors(validationErrors);
				return false;
			}
			setValidationErrors({});
			let isDirty = false;

			if (editingRow && editedValues && editingRow.original) {
				isDirty = Object.keys(editedValues).some((flatKey) => {
					const newValue = editedValues[flatKey];

					if (typeof newValue === "undefined") return false;

					const originalValue = flatKey
						.split(".")
						.reduce((obj, key) => (obj ? obj[key] : undefined), editingRow.original);

					return newValue !== originalValue;
				});
			}

			if (creatingRow) {
				await handleCreateFuel({ values: editedValues, table }, true);
				return true;
			}

			if (editingRow && isDirty) {
				await handleSaveFuel({ values: editedValues, table, row: editingRow }, true);
				return true;
			}

			if (editingRow && !isDirty) {
				resetActiveRow();
				table.setEditingRow(null);
				return true;
			}

			return true;
		}
	}));

	const handleCreateFuel = async ({ values, table }, isAuto = false) => {
		const errors = validateFuel(values, "add", {});
		if (Object.values(errors).some(Boolean)) {
			setValidationErrors(errors);
			return;
		}
		setValidationErrors({});

		const newEntry = transformData(buildFuelPayloadFromValues(values))[0];
		const updatedRowData = [...(fuelData || []), newEntry];

		const payload = updatedRowData
			.map((item) => buildFuelPayloadFromValues(item))
			.flat()
			.map((obj) => ({ ...obj, b4EmissionFactorId: 0 }));

		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B31(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table Emission Factors", isAuto ? "Fuel type has been autosaved successfully" : "Fuel type has been added successfully");
				setFuelData(updatedRowData);
				table.setCreatingRow(null);
				setactiveAction(-1);
				refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table Emission Factors", res?.errorMessage);
			}
		} catch (err) {
			emitToasterMsg("error", "Table Emission Factors", err.errorMessage);
		}
	};

	const handleSaveFuel = async ({ values, table, row }, isAuto = false) => {
		const errors = validateFuel(values, "edit", row);
		if (Object.values(errors).some(Boolean)) {
			setValidationErrors(errors);
			return;
		}

		const enriched = {
			...row.original,
			...values,
			b4EmissionFactorId: row.original.b4EmissionFactorId,
		};
		const updatedEntry = transformData(buildFuelPayloadFromValues(enriched))[0];

		const updatedRowData = (fuelData || []).map((item) => (item.b4EmissionFactorId === enriched.b4EmissionFactorId ? updatedEntry : item));
		const payload = updatedRowData
			.map((item) => buildFuelPayloadFromValues(item))
			.flat()
			.map((obj) => ({ ...obj, b4EmissionFactorId: 0 }));
		try {
			const res = await callApi({
				method: "POST",
				url: API_ROUTES.B31(vesselMstId),
				data: payload,
			});
			if (res?.isSuccess) {
				emitToasterMsg("success", "Table Emission Factors", isAuto ? "Fuel type has been autosaved successfully" : "Fuel type has been Saved successfully");
				setFuelData(updatedRowData);
				table.setEditingRow(null);
				setactiveAction(-1);
				refetchMonitoringData?.();
			} else {
				emitToasterMsg("error", "Table Emission Factors", res?.errorMessage || "Save failed");
			}
		} catch (err) {
			emitToasterMsg("error", "Table Emission Factors", err.errorMessage);
		}
	};

	const handleDeleteFuel = async (indexToDelete) => {
		const newData = fuelData.filter((_, index) => index !== indexToDelete);
		const finalPayload = newData?.map((item) => buildFuelPayloadFromValues(item))?.flat();
		try {
			const b31EmissionFactors = await callApi({
				method: "POST",
				url: API_ROUTES.B31(vesselMstId),
				data: finalPayload,
			});

			if (b31EmissionFactors?.isSuccess) {
				emitToasterMsg("success", "Table Emission Factors", "Fuel type has been Deleted successfully");
				setFuelData(newData);
				setactiveAction(-1);
				setConfirmDelete(false);
				if (typeof refetchMonitoringData === "function") {
					await refetchMonitoringData();
				}
			} else {
				emitToasterMsg("error", "Table Emission Factors", b31EmissionFactors?.errorMessage || "Delete failed");
				setConfirmDelete(false);
			}
		} catch (error) {
			emitToasterMsg("error", "Table Emission Factors", error.errorMessage);
		}
	};

	const getFuelOriginOptions = () => {
		return fuelOriginMsts?.map((origin) => ({
			label: origin.value,
			value: origin.value,
		}));
	};

	const getFuelTypeOptions = (origin) => {
		const entry = fuelOriginMsts.find((o) => o.value === origin);
		return (
			entry?.fuelTypes?.map((type) => ({
				label: type.value,
				value: type.fuelCode,
			})) || []
		);
	};

	const lookupDefaultWtt = (fuelOrigin, fuelType) => {
		const originEntry = fuelOriginMsts.find((o) => o.value === fuelOrigin);
		const typeEntry = originEntry?.fuelTypes?.find((t) => t.fuelCode === fuelType);
		return typeEntry?.wttEfDropDown?.wttEf ?? "";
	};

	const lookupWttValueType = (fuelOrigin, fuelType) => {
		const originEntry = fuelOriginMsts.find((o) => o.value === fuelOrigin);
		const typeEntry = originEntry?.fuelTypes?.find((t) => t.fuelCode === fuelType);
		return typeEntry?.wttEfDropDown?.wttEfValueType ?? "Default";
	};
	const getEmissionFactorsData = (selectedOrigin, selectedType) => {
		const originEntry = fuelOriginMsts.find((origin) => origin.value === selectedOrigin);
		if (!originEntry) return [];
		const typeEntry = originEntry.fuelTypes.find((type) => type.fuelCode === selectedType);
		return typeEntry?.ttwEmissionFactors || [];
	};

	const updateData = (rowIndex, columnId, value) => {
        setFuelData(prev =>
            prev.map((row, index) => {
                if (index === rowIndex) {
                    return {
                        ...row,
                        [columnId]: value,
                    };
                }
                return row;
            })
        );
    };
const handleFuelOriginChange = (e, row, column, table) => {
    const selectedFuelOrigin = e.target.value;
    
    // Only update cache during editing/creating
    if (table.getState().creatingRow || table.getState().editingRow) {
        row._valuesCache.fuelOrigin = selectedFuelOrigin;
        // Clear dependent fields when origin changes
        row._valuesCache.fuelType = "";
        row._valuesCache.nameOfOtherFuel = "";
        row._valuesCache.wttEf = "";
        
        // Also clear gas emission factors from cache
        gases.forEach((gas) => {
            row._valuesCache[gas] = {};
        });
        
        // Update emissionFactorData for UI display only
        const updatedRowData = {
            ...row.original,
            fuelOrigin: selectedFuelOrigin,
            fuelType: "",
            nameOfOtherFuel: "",
            wttEf: "",
        };
        
        gases.forEach((gas) => {
            updatedRowData[gas] = {};
        });
        
        setEmissionFactorData(updatedRowData);
        
    } else {
        // This should only happen when not in edit mode
        table.options.meta?.updateData?.(row.index, column.id, selectedFuelOrigin);
    }
};

const handleFuelTypeChange = (e, row, table) => {
    const selectedFuelType = e.target.value;
    
    // Only update cache during editing/creating
    if (table && (table.getState().creatingRow || table.getState().editingRow)) {
        row._valuesCache.fuelType = selectedFuelType;
        
        // If changing to non-"Other" fuel type, clear the nameOfOtherFuel cache
        if (!selectedFuelType.toLowerCase().includes("other")) {
            row._valuesCache.nameOfOtherFuel = "";
        }
        
        // Calculate emission factors but only store in cache, not update actual data
        const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
        const emissionFactors = getEmissionFactorsData(fuelOrigin, selectedFuelType);
        const defaultWttEf = lookupDefaultWtt(fuelOrigin, selectedFuelType);
        const wttValueType = lookupWttValueType(fuelOrigin, selectedFuelType);
        
        // Update cache with emission factor data
        row._valuesCache.wttEf = defaultWttEf;
        row._valuesCache.wttValueType = wttValueType;
        
        // Update gas emission factors in cache
        gases.forEach((gas) => {
            const found = emissionFactors.find((ef) => 
                ef.ghg === gas.toUpperCase() && ef.legalFrameWork === FUEL_EU_FRAMEWORK
            ) || {};
            
            row._valuesCache[gas] = row._valuesCache[gas] || {};
            row._valuesCache[gas][FUEL_EU_FRAMEWORK] = {
                legalFrameWork: found.legalFrameWork ?? FUEL_EU_FRAMEWORK,
                ttwEf: found.ttwef ?? "",
                slipEf: found.slipEF ?? "",
                factorReference: "Default",
                isTtwEditable: found.ttwefValueType === "Default",
                isSlipEditable: found.isSlipEditable === "Default",
                methodologiesForEFs: "",
            };
        });
        
        // Update emissionFactorData state for UI display (but don't save to actual row data yet)
        const updatedRowData = {
            ...row.original,
            fuelOrigin: fuelOrigin,
            fuelType: selectedFuelType,
            nameOfOtherFuel: selectedFuelType === "Other(OTHER)" ? 
                (row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel || "") : "",
            wttEf: defaultWttEf,
            wttValueType: wttValueType,
        };
        
        gases.forEach((gas) => {
            updatedRowData[gas] = updatedRowData[gas] || {};
            updatedRowData[gas][FUEL_EU_FRAMEWORK] = row._valuesCache[gas]?.[FUEL_EU_FRAMEWORK] || {};
        });
        
        setEmissionFactorData(updatedRowData);
        
    } else {
        // This should only happen when not in edit mode
        table.options.meta?.updateData?.(row.index, "fuelType", selectedFuelType);
        
        // Original logic for non-edit mode (if needed)
        const fuelOrigin = row.original.fuelOrigin;
        const emissionFactors = getEmissionFactorsData(fuelOrigin, selectedFuelType);
        const defaultWttEf = lookupDefaultWtt(fuelOrigin, selectedFuelType);
        const wttValueType = lookupWttValueType(fuelOrigin, selectedFuelType);
        
        const updatedRow = {
            ...row.original,
            fuelType: selectedFuelType,
            nameOfOtherFuel: selectedFuelType === "Other(OTHER)" ? row.original.nameOfOtherFuel : "",
            wttEf: defaultWttEf,
            wttValueType,
        };
        
        gases.forEach((gas) => {
            updatedRow[gas] = updatedRow[gas] || {};
            frameworks.forEach((framework) => {
                const found = emissionFactors.find((ef) => 
                    ef.ghg === gas.toUpperCase() && ef.legalFrameWork === FUEL_EU_FRAMEWORK
                ) || {};
                
                updatedRow[gas][FUEL_EU_FRAMEWORK] = {
                    legalFrameWork: found.legalFrameWork ?? "",
                    ttwEf: found.ttwef ?? "",
                    slipEf: found.slipEF ?? "",
                    factorReference: "Default",
                    isTtwEditable: found.ttwefValueType === "Default",
                    isSlipEditable: found.isSlipEditable === "Default",
                    methodologiesForEFs: "",
                };
            });
        });
        
        // Update the actual row data (only in non-edit mode)
        Object.keys(updatedRow).forEach(key => {
            if (key !== 'index' && key !== 'original' && key !== '_valuesCache') {
                table.options.meta?.updateData?.(row.index, key, updatedRow[key]);
            }
        });
    }
};

	const updateFieldsWhenFuelTypeUnchanged = ({
		row,
		gas,
		framework,
		newValue,
		fuelOrigin,
		fuelType,
		nameOfOtherFuel,
		wttEf,
		bunkeredFuelDensity,
		fuelDescription,
	}) => {
		const updatedPayload = {
			fuelOrigin,
			fuelType,
			nameOfOtherFuel,
			bunkeredFuelDensity,
			fuelDescription,
			co2: { FUEL_EU_FRAMEWORK: {} },
			ch4: { FUEL_EU_FRAMEWORK: {} },
			n2o: { FUEL_EU_FRAMEWORK: {} },
		};
		gases.forEach((g) => {
			frameworks.forEach((f) => {
				const isThisGas = g === gas && f === framework;
				const cached = row._valuesCache?.[g]?.[f] || {};
				const original = row.original?.[g]?.[f] || {};
				const inputValue = isThisGas ? newValue : cached.ttwEf ?? original.ttwEf;
				const isTtwEfChanged = isThisGas && newValue !== "" && newValue !== original?.ttwEf?.toString();
				updatedPayload[g][f] = {
					legalFrameWork: f,
					ttwEf: parseFloat(inputValue),
					slipEf: cached.slipEf ?? original?.slipEf ?? 0,
					factorReference: isTtwEfChanged ? "Defined by user" : cached.factorReference ?? original?.factorReference ?? "Default",
					isTtwEditable: cached.isTtwEditable ?? original?.isTtwEditable ?? false,
					isSlipEditable: cached.isSlipEditable ?? original?.isSlipEditable ?? false,
					methodologiesForEFs: cached.methodologiesForEFs ?? original?.methodologiesForEFs ?? "",
				};
			});
		});

		row._valuesCache = {
			...row._valuesCache,
			fuelOrigin,
			fuelType,
			fuelDescription,
			bunkeredFuelDensity,
			wttEf,
			nameOfOtherFuel,
			co2: { ...row._valuesCache?.co2, ...updatedPayload.co2 },
			ch4: { ...row._valuesCache?.ch4, ...updatedPayload.ch4 },
			n2o: { ...row._valuesCache?.n2o, ...updatedPayload.n2o },
		};

		return updatedPayload;
	};

	const getTtwEfFieldProps = ({ gas, framework }) => {
		return ({ row }) => {
			const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
			const fuelType = row._valuesCache.fuelType || row.original.fuelType;
			const nameOfOtherFuel = row._valuesCache.nameOfOtherFuel || row.original.nameOfOtherFuel;
			const bunkeredFuelDensity = row._valuesCache.bunkeredFuelDensity || row.original.bunkeredFuelDensity;
			const fuelDescription = row._valuesCache.fuelDescription || row.original.fuelDescription;
			const wttEf = row._valuesCache.wttEf || row.original.wttEf;
			const isEditable = emissionFactorData?.[gas]?.[framework]?.isTtwEditable;
			const currentValue = emissionFactorData?.[gas]?.[framework]?.ttwEf;
			return {
				type: "number",
				disabled: !isEditable,
				value: currentValue ?? "",
				onChange: (e) => {
					const newValue = e.target.value;
					const isOriginOrTypeChanged = row.original.fuelOrigin !== fuelOrigin || row.original.fuelType !== fuelType;
					let originalTtwEf = "";
					if (isOriginOrTypeChanged) {
						const emissionFactorsData = getEmissionFactorsData(fuelOrigin, fuelType);
						const originalEntry = emissionFactorsData.find(
							(item) =>
								item.fuelOrigin === fuelOrigin &&
								item.fuelType === fuelType &&
								item.ghg === gas.toUpperCase() &&
								item.legalFrameWork === framework
						);
						originalTtwEf = originalEntry?.ttwef?.toString() ?? "";
						row._valuesCache[gas] = {
							...row._valuesCache[gas],
							[framework]: {
								...row._valuesCache[gas]?.[framework],
								ttwEf: newValue,
								factorReference: newValue !== "" && newValue !== originalTtwEf ? "Defined by user" : "Default",
							},
						};
					} else {
						updateFieldsWhenFuelTypeUnchanged({
							row,
							gas,
							framework,
							newValue,
							fuelOrigin,
							fuelType,
							nameOfOtherFuel,
							bunkeredFuelDensity,
							wttEf,
							fuelDescription,
						});
					}
					setEmissionFactorData((prev) => ({
						...prev,
						[gas]: {
							...prev[gas],
							[framework]: {
								...prev[gas]?.[framework],
								ttwEf: newValue,
								factorReference:
									newValue !== "" && newValue !== row.original?.[gas]?.[framework]?.ttwEf?.toString()
										? "Defined by user"
										: row.original?.[gas]?.[framework]?.factorReference ?? "Default",
							},
						},
					}));
				},
				onKeyDown: (e) => {
					preventScientificNotation(e);
					if (e.key === "ArrowUp" || e.key === "ArrowDown") {
						e.preventDefault();
					}
				},
				onWheel: (e) => {
					e.target.blur();
				},
			};
		};
	};

	const getWttFieldProps = () => ({ row }) => {
	const fuelOrigin = row._valuesCache?.fuelOrigin || row.original?.fuelOrigin;
	const fuelType = row._valuesCache?.fuelType || row.original?.fuelType;
	const wttValueType = lookupWttValueType(fuelOrigin, fuelType);

	let currentValue = row._valuesCache?.wttEf ?? emissionFactorData.wttEf;
	if (currentValue === null || currentValue === undefined) {
		currentValue = "";
	}

	return {
		type: "number",
		disabled: wttValueType === "Fixed",
		value: currentValue,
		onChange: (e) => {
			const newV = e.target.value;

			const numericValue = newV === "" ? null : Number(newV);

			setEmissionFactorData((prev) => ({
				...prev,
				wttEf: numericValue,
			}));

			// Keep cache in sync so UI updates reflect correctly
			row._valuesCache.wttEf = numericValue;
		},
		onKeyDown: preventScientificNotation,
		onWheel: (e) => e.target.blur(),
	};
};


	const fuelBunkeredDensities = useMemo(
		() =>
			fuelBunkerDensities?.map((u) => ({
				label: u.value,
				value: u.code,
			})) ?? [],
		[fuelBunkerDensities]
	);
	const labelByfuelBunkeredDensities = useMemo(() => Object.fromEntries(fuelBunkeredDensities.map((o) => [o.value, o.label])), [fuelBunkeredDensities]);

	const createEmissionColumns = (gas) => {
		const framework = FUEL_EU_FRAMEWORK;
		return {
			id: `${gas}`,
			header: `Emission Factor ${gas?.toUpperCase()} (g${gas?.toUpperCase()}/gFuel)`,
			muiTableHeadCellProps: {
				className: "grouped-header",
			},
			columns: [
				{
					accessorKey: `${gas}.${framework}.factorReference`,
					header: "Factor Reference",
					muiEditTextFieldProps: ({ row }) => ({
						disabled: true,
						value:
							emissionFactorData?.fuelOrigin && emissionFactorData?.fuelType ? emissionFactorData?.[gas]?.[framework]?.factorReference ?? "" : "",
					}),
				},
				{
					accessorKey: `${gas}.${framework}.ttwEf`,
					header: "TTW EF",
					muiEditTextFieldProps: getTtwEfFieldProps({ gas: gas, framework: framework }),
				},
				{
					accessorKey: `${gas}.${framework}.slipEf`,
					header: "Slip EF",
					muiEditTextFieldProps: ({ row }) => ({
						disabled: true,
						value: emissionFactorData?.fuelOrigin && emissionFactorData?.fuelType ? emissionFactorData?.[gas]?.[framework]?.slipEf ?? "" : "",
					}),
				},
				{
					accessorKey: `${gas}.${framework}.methodologiesForEFs`,
					header: "Methodologies for determining the EF's",
					muiEditTextFieldProps: ({ row }) => {
						const isEditable = emissionFactorData?.[gas]?.[framework]?.isTtwEditable;
						const currentValue = emissionFactorData?.[gas]?.[framework]?.methodologiesForEFs ?? "";

						return {
							type: "text",
							disabled: !isEditable,
							inputProps: {
								maxLength: 1000,
							},
							value: currentValue,
							onChange: (e) => {
								const newValue = e.target.value;
								setEmissionFactorData((prev) => ({
									...prev,
									[gas]: {
										...prev[gas],
										[framework]: {
											...prev[gas]?.[framework],
											methodologiesForEFs: newValue,
										},
									},
								}));
								row._valuesCache[gas] = {
									...row._valuesCache[gas],
									[framework]: {
										...row._valuesCache[gas]?.[framework],
										methodologiesForEFs: newValue,
									},
								};
							},
						};
					},
				},
			],
		};
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "fuelOrigin",
				header: "Fuel Origin",
				editVariant: "select",
				editSelectOptions: getFuelOriginOptions,
				muiEditTextFieldProps: ({ row, table,column }) => ({
					required: true,
					error: !!validationErrors?.fuelOrigin,
					helperText: validationErrors?.fuelOrigin,
					onFocus: () =>
						setValidationErrors({
							...validationErrors,
							fuelOrigin: undefined,
							nameOfOtherFuel: undefined,
						}),
						
					onChange: (e) => {
						handleFuelOriginChange(e, row, column, table)
					},
				}),
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},

			{
				accessorKey: "fuelType",
				header: "Fuel Type",
				editVariant: "select",
				size: 280,
				editSelectOptions: ({ row }) => {
					const origin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
					return getFuelTypeOptions(origin) || [];
				},
				Cell: ({ cell, row }) => {
					const fuelOrigin = row.original.fuelOrigin || row._valuesCache.fuelOrigin;
					const options = getFuelTypeOptions(fuelOrigin);
					const value = cell.getValue();
					const matchedOption = options?.find((opt) => opt.value === value);
					return matchedOption?.label || value || "-";
				},
				muiEditTextFieldProps: ({ row,table }) => {
					const fuelOrigin = row._valuesCache.fuelOrigin || row.original.fuelOrigin;
					return {
						value: row._valuesCache.fuelType || row.original.fuelType || "",
						disabled: !fuelOrigin,
						required: true,
						error: !!validationErrors?.fuelType,
						helperText: validationErrors?.fuelType,
						onFocus: () =>
							setValidationErrors({
								...validationErrors,
								fuelType: undefined,
								nameOfOtherFuel: undefined,
							}),
						onChange: (e) => handleFuelTypeChange(e, row,table),
						SelectProps: {
							MenuProps: {
								disableScrollLock: true,
								PaperProps: {
									sx: {
										maxHeight: 250,
										maxWidth: 250,
										boxShadow: 3,
										overflow: "auto",
										whiteSpace: "nowrap",
										"&::-webkit-scrollbar": {
											height: "8px",
											width: "8px",
										},
										"&::-webkit-scrollbar-thumb": {
											backgroundColor: "darkgray",
											borderRadius: "3px",
										},
										"&::-webkit-scrollbar-track": {
											backgroundColor: "#f0f0f0",
										},
									},
								},
								anchorOrigin: {
									vertical: "bottom",
									horizontal: "left",
								},
								transformOrigin: {
									vertical: "top",
									horizontal: "left",
								},
								getContentAnchorEl: null,
							},
						},
					};
				},
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
			{
				accessorKey: "nameOfOtherFuel",
				header: "Name of the other fuel type",
				muiEditTextFieldProps: ({ row }) => {

					const fuelType = row._valuesCache?.fuelType || row.original?.fuelType || "";
					const fuelOrigin = row._valuesCache?.fuelOrigin || row.original?.fuelOrigin || "";
					const isOther =
					 	fuelOrigin.toLowerCase().includes("biofuel") &&
						fuelType.toLowerCase().includes("other");
					const nameOfOtherFuel = row._valuesCache?.nameOfOtherFuel ?? row.original?.nameOfOtherFuel ?? "";
					return {
						type: "text",
						value: nameOfOtherFuel,
						inputProps: { maxLength: 255 },
						disabled: !isOther,
						error: !!validationErrors?.nameOfOtherFuel,
						helperText: validationErrors?.nameOfOtherFuel,
						onFocus: () =>
							setValidationErrors({
								...validationErrors,				
								nameOfOtherFuel: undefined,
							}),
						onChange: (e) => {
							row._valuesCache.nameOfOtherFuel =e.target.value;
						},
					};
				},
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
			{
				accessorKey: "bunkeredFuelDensity",
				header: "Method for determination of density of fuel bunkered",
				editVariant: "select",
				editSelectOptions: fuelBunkeredDensities,
				Cell: ({ cell }) => {
					const value = cell.getValue();
					if (Array.isArray(value)) {
						return value.map((v) => labelByfuelBunkeredDensities[v] ?? v).join(", ");
					}
					return labelByfuelBunkeredDensities[value] ?? value;
				},
				   muiEditTextFieldProps: ({ cell, row }) => ({
									select: true,
									required: false,
									value: cell.getValue() || [],
									SelectProps: {
										multiple: true,
										renderValue: (selected) => {
											if (!selected || selected.length === 0) return 'Select density method(s)';
											return (
												<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
													{selected.map((code) => (
														<Chip
															key={code}
															label={labelByfuelBunkeredDensities?.[code] ?? code}
															onDelete={(e) => {
																e.stopPropagation();
																const newValue = selected.filter((v) => v !== code);
																row._valuesCache["bunkeredFuelDensity"] = newValue;
																setValidationErrors?.((prev) => ({
																	...prev,
																	bunkeredFuelDensity: undefined,
																}));
															
															}}
															onMouseDown={(e) => e.stopPropagation()}
														/>
													))}
												</Box>
											);
										},
										MenuProps: {
											disableScrollLock: true,
											PaperProps: {
												sx: {
													maxHeight: 250,
													maxWidth: 280,
													overflowY: "auto",
													'&::-webkit-scrollbar': {
														width: "8px"
													},
													'&::-webkit-scrollbar-thumb': {
														backgroundColor: 'darkgray',
														borderRadius: '3px',
													},
													'&::-webkit-scrollbar-track': {
														backgroundColor: '#f0f0f0',
													},
												},
											},
											anchorOrigin: {
												vertical: "bottom",
												horizontal: "left",
											},
											transformOrigin: {
												vertical: "top",
												horizontal: "left",
											},
											getContentAnchorEl: null,
										},
									},
								onChange: (event) => {
						const selected = event.target.value;
						row._valuesCache.bunkeredFuelDensity = selected;
					},
								}),
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},

			{
				accessorKey: "fuelDescription",
				header: "Fuel Description",
				muiEditTextFieldProps: ({ row }) => {
					return {
						type: "text",
						inputProps: {
							maxLength: 1000,
						},
					};
				},
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
			{
				accessorKey: "wttEf",
				header: "WTT EF (gGHG / gFuel)",
				muiEditTextFieldProps: getWttFieldProps(),
				muiTableHeadCellProps: { sx: { borderBottom: "none", borderRight: "1px solid lightgray" } },
			},
			...gases?.map((gas) => createEmissionColumns(gas)),
		],
		[validationErrors, fuelData, emissionFactorData]
	);

	const table = useMaterialReactTable({
		columns,
		data: fuelData || [],
		meta:{
            updateData,
        },
 
		enableStickyHeader: true,
		enableSorting: false,
		enableRowSelection: false,
		enableColumnPinning: false,
		enableTopToolbar: true,
		enableBottomToolbar: false,
		enableColumnActions: false,
		createDisplayMode: "row",
		editDisplayMode: "row",
		enableEditing: true,
		enablePagination: false,
		muiTableHeadCellProps: {
			sx: {
				color: "rgb(255 255 255)",
				backgroundColor: "#042a4c",
				position: "sticky",
				borderRight: "1px solid lightgray",
				"&:last-child": {
					borderRight: "none",
				},
			},
		},
		muiTablePaperProps: {
			sx: {
				boxShadow: "0 0 8px 2px rgba(0, 0, 0, 0.1)",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "350px",
				overflowY: "auto",
			},
		},
		displayColumnDefOptions: {
			"mrt-row-actions": {
				muiTableHeadCellProps: {
					sx: {
						borderBottom: "none",
						borderRight: "1px solid lightgray",
					},
				},
			},
		},
		muiTableBodyCellProps: ({ row, cell }) => {
			return {
				sx: {
					borderRight: "1px solid lightgray",
					"&:last-child": {
						borderRight: "none",
					},
					color: "",
				},
			};
		},
		muiTableBodyRowProps: ({ row }) => ({
			sx: {
				"&:not(:last-of-type)": {
					borderBottom: "1px solid lightgray",
				},
				height: "60px",
				verticalAlign: "middle",
			},
		}),
		muiTableHeadRowProps: {
			sx: {
				borderBottom: "2px solid lightgray",
			},
		},
		getRowId: (row) => {
			return row?.original?.b4EmissionFactorId;
		},
		onCreatingRowSave: handleCreateFuel,
		onEditingRowCancel: resetActiveRow,
		onCreatingRowCancel: resetActiveRow,
		onEditingRowSave: handleSaveFuel,
		renderToolbarInternalActions: ({ table }) => <></>,
		renderRowActions: ({ row, table }) => {
			return (
				<Box sx={{ display: "flex", gap: "1rem" }}>
					<Tooltip title="Edit">
						<IconButton style={{ padding: "0px" }} onClick={() => handleFuelActions(table, row, RowActions.Edit)}>
							<EditIcon />
						</IconButton>
					</Tooltip>
					<Tooltip title="Delete">
						<IconButton style={{ padding: "0px" }} color="error" onClick={() => handleDeleteBtnClick(row?.index, true)}>
							<DeleteIcon />
						</IconButton>
					</Tooltip>
				</Box>
			);
		},
		renderTopToolbarCustomActions: ({ table }) => (
			<div className="mt-1">
				<Button
					startIcon={<Icon icon={ICONS.ADD} />}
					onClick={() => {
						handleFuelActions(table, null, RowActions.Add);
					}}
					disabled={!!table.getState().creatingRow}
				>
					Add Fuel type
				</Button>
			</div>
		),
	});

	return (
		<>
			<div className="b4-emission-factors mt-1">
				<MaterialReactTable table={table} />
			</div>
			<div className="confirmation-modal-container">
				<Modal
					isOpen={confirmDelete}
					modalHeader
					padding={"70px"}
					headerHeight={"50px"}
					showFooter
					onCloseClickListener={() => {
						setConfirmDelete(false);
					}}
					footer={
						<div className="d-flex justify-content-end fixed-action-btns p-4">
							<Button
								className="modal-delete-btn"
								onClick={() => { handleDeleteFuel(deletingRowIndex) }}
								primaryColor={COLORS.RED.DARK}
								primaryBackgroundColor={COLORS.RED.LIGHT}
							>
								Delete
							</Button>
							<Button
								className="modal-cancel-btn"
								onClick={() => {
									setConfirmDelete(false);
								}}
							>
								Cancel
							</Button>
						</div>
					}
				>
					<div>Are you sure you want to delete this fuel type?</div>
				</Modal>
			</div>
		</>
	);
});

export default FuelEuEmissionFactors;
